import { useCallback, useEffect, useState } from "react";
import { AgentOSLayout } from "@/components/agent-os/AgentOSLayout";
import { apiFetch, Empty, formatDateTime, inputClass, Pill, Spinner } from "@/components/agent-os/shared";

export default function AgentOSApprovals() {
    const [items, setItems] = useState(null);
    const [status, setStatus] = useState("pending");
    const [reasons, setReasons] = useState({});

    const load = useCallback(() => {
        apiFetch(`/approvals${status ? `?status=${status}` : ""}`).then((r) => (r.ok ? r.json() : [])).then(setItems);
    }, [status]);
    useEffect(() => { load(); }, [load]);

    const decide = async (item, decision) => {
        await apiFetch(`/approvals/${item.approval_id}/decision`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ decision, reason: reasons[item.approval_id] || "" }),
        });
        load();
    };

    return (
        <AgentOSLayout title="Human Approvals" subtitle="High-impact and sensitive actions wait here for explicit Afrinexa administrator review.">
            <div className="mb-6 flex gap-2" data-testid="approval-filters">
                {["pending", "approved", "rejected", ""].map((value) => (
                    <button key={value || "all"} type="button" data-testid={`approval-filter-${value || "all"}`} onClick={() => setStatus(value)} className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] ${status === value ? "border-forest bg-forest text-ivory" : "border-ink/15 text-ink/55"}`}>{value || "all"}</button>
                ))}
            </div>
            {!items ? <Spinner /> : items.length === 0 ? <Empty title="No approvals" copy="Approval requests will appear here when an agent crosses a configured risk threshold." /> : (
                <div className="space-y-4" data-testid="approvals-list">
                    {items.map((item) => (
                        <article key={item.approval_id} data-testid={`approval-${item.approval_id}`} className="border border-ink/10 bg-ivory p-6">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <div className="flex flex-wrap gap-2"><Pill value={item.status} /><Pill value={item.risk_level} risk /></div>
                                    <h2 className="mt-3 font-serif text-2xl font-medium">{item.action}</h2>
                                    <p className="mt-1 text-xs text-ink/50">{item.agent_id} · {item.tool_id || "no tool"} · {item.task_id || "no task"} · {formatDateTime(item.created_at)}</p>
                                </div>
                            </div>
                            {item.payload_summary && <p className="mt-4 border-l-2 border-champagne pl-4 text-sm text-ink/65">{item.payload_summary}</p>}
                            {item.status === "pending" && (
                                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                                    <input data-testid={`approval-reason-${item.approval_id}`} value={reasons[item.approval_id] || ""} onChange={(e) => setReasons({ ...reasons, [item.approval_id]: e.target.value })} placeholder="Decision note…" className={`${inputClass} flex-1`} />
                                    <button type="button" data-testid={`approval-approve-${item.approval_id}`} onClick={() => decide(item, "approved")} className="bg-forest px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-ivory hover:bg-forest-deep">Approve</button>
                                    <button type="button" data-testid={`approval-reject-${item.approval_id}`} onClick={() => decide(item, "rejected")} className="border border-red-900/25 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-red-900">Reject</button>
                                </div>
                            )}
                            {item.decision_reason && <p className="mt-4 text-xs text-ink/45">Decision note: {item.decision_reason}</p>}
                        </article>
                    ))}
                </div>
            )}
        </AgentOSLayout>
    );
}
